import { SocialPlatform } from '@mg/shared';

/**
 * The destinations "Add destination" made in this browser tab, each with the platform its copied
 * words were written for.
 *
 * Adding a destination seeds a new post by COPYING the open post's words to another account, and
 * words written for one platform rarely fit the next (X takes 280 characters, LinkedIn 3,000). So the
 * new destination opens asking for a rewrite - the notice the Platform picker raises when a post is
 * moved. Nothing on the post itself says its words were copied: a set the Content Strategist wrote
 * arrives the same way, as ordinary posts in one set, but with words written for each platform, and
 * it must open without the notice. So the seeds made here are remembered here, until the new
 * destination is saved (its words then belong to its own platform) or the notice is dismissed.
 *
 * In sessionStorage rather than in the composer: choosing a destination tab mounts a new composer,
 * and a reload should not forget what was just copied. Best-effort: where storage is unavailable
 * nothing is remembered, and the new destination opens without the notice, as it always did.
 */
const PREFIX = 'mg.copiedText.v1:';
const PLATFORMS = new Set<string>(Object.values(SocialPlatform));

/** Per workspace as well as per post: what is remembered never follows a workspace switch. */
const keyOf = (tenantKey: string, postId: string) => `${PREFIX}${tenantKey}:${postId}`;

/** The post `postId` was just given words written for `from`. */
export function rememberCopiedText(tenantKey: string, postId: string, from: SocialPlatform): void {
  try {
    sessionStorage.setItem(keyOf(tenantKey, postId), from);
  } catch {
    // Storage unavailable (blocked, private mode): nothing is remembered.
  }
}

/**
 * The platform the post's words were written for, when this tab copied them there and they have not
 * been saved since; null otherwise. Checked on read: a value that is not a platform is ignored.
 */
export function copiedTextFrom(tenantKey: string, postId: string): SocialPlatform | null {
  try {
    const stored = sessionStorage.getItem(keyOf(tenantKey, postId));
    return stored != null && PLATFORMS.has(stored) ? (stored as SocialPlatform) : null;
  } catch {
    return null;
  }
}

/** The post's words are its own now - saved, or the notice was dismissed. */
export function forgetCopiedText(tenantKey: string, postId: string): void {
  try {
    sessionStorage.removeItem(keyOf(tenantKey, postId));
  } catch {
    // Storage unavailable: there is nothing to forget.
  }
}
